import type {
  AdminRule,
  AdminRulesResponse,
  CreateAdminRuleBody,
  UpdateAdminRuleBody,
} from '@/api/(admin)/rules/types';
import { API_ROOT_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';

/** Pure RPC over the admin rules endpoints, `/api/admin/certification-rules`. */

/**
 * The list — `GET /api/admin/certification-rules`, the migrated route. Takes a
 * pre-built query string; paging, sort and filter live in the hook.
 */
export async function fetchAdminRules(qs: string): Promise<AdminRulesResponse> {
  return apiFetch<AdminRulesResponse>(
    `/api/admin/certification-rules?${qs}`,
    {},
    API_ROOT_URL,
  );
}

/**
 * Creates one rule — `POST /api/admin/certification-rules`, the migrated
 * route. Answers 201 with the stored rule in a `{ data }` envelope.
 */
export async function createAdminRule(
  body: CreateAdminRuleBody,
): Promise<{ data: AdminRule }> {
  return apiFetch<{ data: AdminRule }>(
    '/api/admin/certification-rules',
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
    API_ROOT_URL,
  );
}

/**
 * `PUT /api/admin/certification-rules/:id`, the migrated route — a full
 * replacement: the service writes all three columns from this body, so
 * anything left out is blanked rather than kept. Answers the stored rule in a
 * `{ data }` envelope.
 *
 * The service records the change in the rule's history, with the
 * expression before and after, so an overwrite here is recoverable.
 */
export async function updateAdminRule(
  id: string,
  body: UpdateAdminRuleBody,
): Promise<{ data: AdminRule }> {
  return apiFetch<{ data: AdminRule }>(
    `/api/admin/certification-rules/${encodeURIComponent(id)}`,
    { method: 'PUT', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
}

/**
 * Deletes one rule — `DELETE /api/admin/certification-rules/:id`, the migrated
 * route; answers 204. Like the PUT, the service records it in the rule's
 * history, so the deleted rule's expression survives the delete.
 */
export async function deleteAdminRule(id: string): Promise<void> {
  await apiFetch<void>(
    `/api/admin/certification-rules/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    API_ROOT_URL,
  );
}
